import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';
import { normalizeColorForMedia } from '@theme/display-text-config';

/**
 * Build a Shopify CDN srcset from a width= URL template.
 * @param {string} url
 * @param {string} widthsCsv
 */
function buildShopifySrcset(url, widthsCsv) {
  if (!url) return '';
  const widths = String(widthsCsv || '')
    .split(',')
    .map((w) => w.trim())
    .filter(Boolean);
  if (!widths.length) return '';
  return widths
    .map((w) => {
      const sized = /([?&])width=\d+/.test(url)
        ? url.replace(/([?&])width=\d+/, `$1width=${w}`)
        : `${url}${url.includes('?') ? '&' : '?'}width=${w}`;
      return `${sized} ${w}w`;
    })
    .join(', ');
}

/**
 * Materialize deferred PDP media shells (no src until visible / selected color).
 * @param {ParentNode | null | undefined} root
 * @param {{ includeHidden?: boolean }} [options]
 */
export function hydrateDeferredProductMedia(root, options = {}) {
  if (!root) return;
  const includeHidden = Boolean(options.includeHidden);

  const isEffectivelyHidden = (el) => {
    if (!(el instanceof HTMLElement)) return true;
    if (includeHidden) return false;
    let node = el;
    while (node && node !== root) {
      if (node instanceof HTMLElement) {
        if (node.hasAttribute('hidden') || node.getAttribute('aria-hidden') === 'true') return true;
        if (node.style.display === 'none') return true;
      }
      node = node.parentElement;
    }
    return false;
  };

  root.querySelectorAll('.product-media--deferred[data-deferred-image]').forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    if (isEffectivelyHidden(el)) return;
    if (el.querySelector('img.product-media__image')) return;

    const url = el.dataset.deferredImage || '';
    if (!url) return;

    const img = document.createElement('img');
    img.className = 'product-media__image';
    img.alt = el.dataset.deferredAlt || '';
    img.decoding = 'async';
    img.loading = 'lazy';
    const sizes = el.dataset.deferredSizes || '100vw';
    img.sizes = sizes;
    const srcset = buildShopifySrcset(url, el.dataset.deferredWidths || '400,600,800,1000,1200');
    if (srcset) img.srcset = srcset;
    img.src = url;
    el.appendChild(img);
    el.classList.remove('product-media--deferred');
  });

  root.querySelectorAll('.dialog-thumbnails-list__thumbnail--deferred[data-deferred-image]').forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    if (isEffectivelyHidden(el)) return;
    if (el.querySelector('img')) return;
    const url = el.dataset.deferredImage || '';
    if (!url) return;
    const img = document.createElement('img');
    img.src = url;
    img.alt = el.dataset.deferredAlt || '';
    img.loading = 'lazy';
    img.decoding = 'async';
    el.appendChild(img);
    el.classList.remove('dialog-thumbnails-list__thumbnail--deferred');
  });
}

/**
 * Parse compact other-color map (shared PDP + quick-add):
 * color::displayUrl::position::id::aspect::alt||...
 * @param {string} raw
 * @returns {Record<string, Array<{u:string,p:number,i:string,z:string,t:string,r:number,alt:string}>>}
 */
export function parsePdpDeferredMediaMap(raw) {
  /** @type {Record<string, Array<{u:string,p:number,i:string,z:string,t:string,r:number,alt:string}>>} */
  const map = {};
  if (!raw) return map;
  let decoded = raw;
  try {
    if (decoded.charAt(0) === '"') decoded = JSON.parse(decoded);
  } catch {
    /* keep raw */
  }

  const withWidth = (url, width) => {
    if (!url) return '';
    const w = String(width);
    if (/([?&])width=\d+/.test(url)) {
      return url.replace(/([?&])width=\d+/, `$1width=${w}`);
    }
    return `${url}${url.includes('?') ? '&' : '?'}width=${w}`;
  };

  for (const row of String(decoded).split('||')) {
    if (!row) continue;
    const colorEnd = row.indexOf('::');
    if (colorEnd < 0) continue;
    const colorRaw = row.slice(0, colorEnd);
    const color = normalizeColorForMedia(colorRaw) || colorRaw;
    let remaining = row.slice(colorEnd + 2);
    const fields = [];
    const peelCount = (remaining.match(/::/g) || []).length >= 6 ? 6 : 4;
    for (let i = 0; i < peelCount; i++) {
      const sep = remaining.lastIndexOf('::');
      if (sep < 0) break;
      fields.unshift(remaining.slice(sep + 2));
      remaining = remaining.slice(0, sep);
    }
    if (!remaining) continue;

    let position;
    let id;
    let zoom;
    let thumb;
    let aspect;
    let alt;
    if (fields.length >= 6) {
      [position, id, zoom, thumb, aspect, alt] = fields;
    } else if (fields.length >= 4) {
      [position, id, aspect, alt] = fields;
      zoom = withWidth(remaining, 1600);
      thumb = withWidth(remaining, 300);
    } else {
      continue;
    }

    if (!map[color]) map[color] = [];
    map[color].push({
      u: remaining,
      p: Number(position) || 1,
      i: String(id || ''),
      z: zoom || withWidth(remaining, 1600),
      t: thumb || withWidth(remaining, 300),
      r: Number(aspect) || 1,
      alt: alt || '',
    });
  }
  Object.keys(map).forEach((key) => {
    map[key].sort((a, b) => a.p - b.p);
  });
  return map;
}

/**
 * Inject slideshow (+ zoom) shells for a color from the deferred map (once).
 * Used by PDP full-width gallery and quick-add drawer (selected color is SSR'd).
 * @param {HTMLElement | null | undefined} galleryRoot
 * @param {string} colorValue
 * @returns {boolean}
 */
export function ensureDeferredColorMediaInRoot(galleryRoot, colorValue) {
  const color = normalizeColorForMedia(colorValue);
  if (!(galleryRoot instanceof HTMLElement) || !color) return false;

  const isVideoSlide = (slide) =>
    slide.getAttribute('data-media-is-video') === 'true' ||
    slide.classList.contains('product-media-container--video') ||
    slide.classList.contains('product-media-container--external_video');

  // Videos for other colors are SSR'd; do not skip image injection because a video slide exists.
  const hasImageForColor = Array.from(
    galleryRoot.querySelectorAll('slideshow-slide[data-media-color]')
  ).some(
    (slide) =>
      normalizeColorForMedia(slide.getAttribute('data-media-color') || '') === color &&
      !isVideoSlide(slide)
  );
  if (hasImageForColor) return false;

  const existingMediaIds = new Set(
    Array.from(galleryRoot.querySelectorAll('slideshow-slide[data-media-id]')).map((slide) =>
      String(slide.getAttribute('data-media-id') || '')
    )
  );

  if (!galleryRoot._pzDeferredMediaMap) {
    const script = galleryRoot.querySelector('script[data-pdp-deferred-media-map]');
    galleryRoot._pzDeferredMediaMap = parsePdpDeferredMediaMap(script?.textContent || '');
  }

  /** @type {Array<{u:string,p:number,i:string,z:string,t:string,r:number,alt:string}> | undefined} */
  let entries = galleryRoot._pzDeferredMediaMap[color];
  if (!entries?.length) {
    const map = galleryRoot._pzDeferredMediaMap;
    for (const key of Object.keys(map)) {
      if (normalizeColorForMedia(key) === color) {
        entries = map[key];
        break;
      }
    }
  }
  if (!entries?.length) return false;

  const slideshow =
    galleryRoot.querySelector('slideshow-slides') || galleryRoot.querySelector('slideshow-component');
  const slidesHost =
    galleryRoot.querySelector('slideshow-slides') ||
    (slideshow instanceof HTMLElement ? slideshow.querySelector('slideshow-slides') : null);
  if (!(slidesHost instanceof HTMLElement)) return false;

  const pdpSizes = galleryRoot.getAttribute('data-pdp-media-sizes') || '100vw';
  const zoomSizes = galleryRoot.getAttribute('data-zoom-media-sizes') || '100vw';
  const fragment = document.createDocumentFragment();
  const zoomList = galleryRoot.querySelector('zoom-dialog .dialog-zoomed-gallery');
  const zoomThumbs = galleryRoot.querySelector('zoom-dialog .dialog-thumbnails-list');
  const zoomFrag = document.createDocumentFragment();
  const thumbFrag = document.createDocumentFragment();

  entries.forEach((entry, index) => {
    if (existingMediaIds.has(String(entry.i))) return;

    const slide = document.createElement('slideshow-slide');
    slide.className =
      'product-media-container product-media-container--image product-media-container--contain';
    slide.setAttribute('aria-hidden', 'true');
    slide.setAttribute('hidden', '');
    slide.setAttribute('ref', 'slides[]');
    slide.dataset.mediaColor = color;
    slide.dataset.mediaId = entry.i;
    slide.dataset.mediaPosition = String(entry.p);
    slide.dataset.mediaRole = entry.p === 1 ? 'primary' : 'gallery';
    slide.dataset.mediaAlt = entry.alt || `${color}-${entry.p}`;
    slide.dataset.mediaSrc = entry.u;
    slide.dataset.mediaIsVideo = 'false';
    slide.style.setProperty('--media-preview-ratio', String(entry.r || 1));
    slide.style.setProperty('--product-media-fit', 'contain');

    const shell = document.createElement('div');
    shell.className = 'product-media product-media--deferred';
    shell.style.setProperty('--ratio', String(entry.r || 1));
    shell.dataset.mediaId = entry.i;
    shell.setAttribute('data-deferred-image', entry.u);
    shell.setAttribute('data-deferred-widths', '400,600,800,1000,1200');
    shell.setAttribute('data-deferred-sizes', pdpSizes);
    shell.setAttribute('data-deferred-alt', entry.alt || '');
    slide.appendChild(shell);
    fragment.appendChild(slide);

    if (zoomList instanceof HTMLElement) {
      const li = document.createElement('li');
      li.id = `product-${entry.i}-deferred-${index}`;
      li.className =
        'product-media-container product-media-container--image product-media-container--contain product-media-container--zoomable';
      li.style.setProperty('--media-preview-ratio', String(entry.r || 1));
      li.style.display = 'none';
      li.dataset.mediaColor = color;
      li.dataset.mediaId = entry.i;
      li.setAttribute('hidden', '');
      li.setAttribute('ref', 'media[]');

      const wrap = document.createElement('drag-zoom-wrapper');
      wrap.className = 'product-media__drag-zoom-wrapper';
      const zShell = document.createElement('div');
      zShell.className = 'product-media product-media--deferred';
      zShell.style.setProperty('--ratio', String(entry.r || 1));
      zShell.dataset.mediaId = entry.i;
      zShell.setAttribute('data-deferred-image', entry.z || entry.u);
      zShell.setAttribute('data-deferred-widths', '600,800,1000,1200,1600');
      zShell.setAttribute('data-deferred-sizes', zoomSizes);
      zShell.setAttribute('data-deferred-alt', entry.alt || '');
      wrap.appendChild(zShell);
      li.appendChild(wrap);
      zoomFrag.appendChild(li);
    }

    if (zoomThumbs instanceof HTMLElement) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className =
        'button button-unstyled dialog-thumbnails-list__thumbnail dialog-thumbnails-list__thumbnail--deferred';
      btn.style.setProperty('--aspect-ratio', String(entry.r || 1));
      btn.style.setProperty('--gallery-aspect-ratio', String(entry.r || 1));
      btn.style.display = 'none';
      btn.dataset.mediaColor = color;
      btn.setAttribute('hidden', '');
      btn.setAttribute('data-deferred-image', entry.t || entry.u);
      btn.setAttribute('data-deferred-alt', entry.alt || '');
      btn.setAttribute('aria-label', entry.alt || color);
      thumbFrag.appendChild(btn);
    }
  });

  slidesHost.appendChild(fragment);
  if (zoomList instanceof HTMLElement) zoomList.appendChild(zoomFrag);
  if (zoomThumbs instanceof HTMLElement) zoomThumbs.appendChild(thumbFrag);
  return true;
}

/**
 * Resolve gallery root that holds the deferred map (PDP wrapper or quick-add drawer body).
 * @param {Element | null | undefined} fromEl
 * @returns {HTMLElement | null}
 */
export function resolveDeferredMediaGalleryRoot(fromEl) {
  if (!(fromEl instanceof Element)) return null;
  const root =
    fromEl.closest('[data-media-gallery-full-width]') ||
    fromEl.closest('.quick-add-modal__drawer-body') ||
    fromEl.closest('.quick-add-modal__media');
  return root instanceof HTMLElement ? root : null;
}

if (typeof window !== 'undefined') {
  window.hydrateDeferredProductMedia = hydrateDeferredProductMedia;
  window.parsePdpDeferredMediaMap = parsePdpDeferredMediaMap;
  window.ensureDeferredColorMediaInRoot = ensureDeferredColorMediaInRoot;
  window.resolveDeferredMediaGalleryRoot = resolveDeferredMediaGalleryRoot;
  window.normalizeColorForMedia = normalizeColorForMedia;
}

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    // PDP section, or quick-add drawer (<dialog> / <quick-add-dialog>)
    const target =
      this.closest('.shopify-section') ||
      this.closest('quick-add-dialog') ||
      this.closest('dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * Full-width PDP: `product-media-gallery-full-width.js` owns client color filtering (INP).
   * Quick-add: this component still owns instant client filtering.
   * If the live DOM has no visible slides for the new color key, fall through and replace from server HTML.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail?.data?.html;
    const colorValue = event.detail?.data?.colorValue || '';
    const mediaOnly = Boolean(event.detail?.data?.mediaOnly);
    const isFullWidthPdpGallery =
      this.closest('[data-media-gallery-full-width]') != null ||
      this.hasAttribute('data-media-gallery-full-width');
    const isQuickAddMedia = this.closest('.quick-add-modal__media') != null;

    // PDP full-width: skip duplicate client filter — full-width script already filtered.
    if (isFullWidthPdpGallery && !event.detail?.data?.newProduct && colorValue) {
      if (mediaOnly || !source) return;
      // After section fetch: keep live gallery when that color is already visible.
      if (this.#hasVisibleClientColorMatches(colorValue)) return;
      // No visible matches — replace from fetched section HTML below.
    } else if (isQuickAddMedia && !event.detail?.data?.newProduct && colorValue) {
      // Instant client filter — do not wait for section fetch (critical for quick-add drawer).
      if (this.#applyClientColorFilter(colorValue)) {
        if (mediaOnly || !source) return;
        // Color already visible in cloned gallery — skip expensive DOM replace.
        return;
      }
      if (mediaOnly || !source) return;
      // No matching slides in live DOM — replace from fetched section (selected color).
    } else if (!source) {
      return;
    }

    if (!source) return;

    const section = this.closest('.shopify-section');
    const sectionId = section?.id;
    // Prefer the gallery from the same section (full-page fetches can include other galleries)
    const sectionFromSource = sectionId ? source.getElementById(sectionId) : null;
    const newMediaGallery =
      sectionFromSource?.querySelector('media-gallery') || source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    const pitcLayout = this.dataset.pitcLayout || '';
    if (
      (pitcLayout === 'one_grid' || pitcLayout === 'two_grid') &&
      this.querySelector('.media-gallery__grid') &&
      !newMediaGallery.querySelector('.media-gallery__grid')
    ) {
      // Keep current stacked grid — replacement is carousel-only and would collapse to one slide
      return;
    }

    // Zoom may live in the section (PDP) or portaled onto quick-add-dialog
    const zoomHost =
      this.closest('.shopify-section') ||
      this.closest('quick-add-dialog') ||
      this.closest('dialog');
    const oldZoomDialog =
      zoomHost?.querySelector(':scope > zoom-dialog') ||
      zoomHost?.querySelector('zoom-dialog');
    const newZoomDialog =
      sectionFromSource?.querySelector('zoom-dialog') || source.querySelector('zoom-dialog');

    if (oldZoomDialog && newZoomDialog) {
      const newZoomDialogClone = newZoomDialog.cloneNode(true);
      oldZoomDialog.replaceWith(newZoomDialogClone);
    }

    const parentMedia = this.closest(
      '.product-information__media, .product-two-column__media, .quick-add-modal__media'
    );
    this.replaceWith(newMediaGallery);

    // Quick-add (and any host) can re-apply carousel / video / attribute wiring
    parentMedia?.dispatchEvent(
      new CustomEvent('media-gallery:replaced', {
        bubbles: true,
        detail: {
          colorValue: event.detail?.data?.colorValue || '',
          mediaGallery: newMediaGallery,
        },
      })
    );
  };

  /**
   * Show/hide slides by data-media-color without waiting on network HTML.
   * Always lands on the first matching media (position 1 / pagination 1).
   * @param {string} colorValue
   * @returns {boolean} true when at least one matching slide is shown
   */
  #applyClientColorFilter(colorValue) {
    const normalized = normalizeColorForMedia(colorValue);
    if (!normalized) return false;

    // Inject other-color shells from deferred map before filtering (selected color is SSR'd).
    const galleryRoot = resolveDeferredMediaGalleryRoot(this);
    if (galleryRoot) ensureDeferredColorMediaInRoot(galleryRoot, normalized);

    const slides = this.querySelectorAll('slideshow-slide:not([data-gallery-clone])');
    if (!slides.length) return false;

    let matchCount = 0;
    /** @type {HTMLElement | null} */
    let firstMatch = null;
    let firstPosition = Number.POSITIVE_INFINITY;

    slides.forEach((slide) => {
      if (!(slide instanceof HTMLElement)) return;
      const role = slide.getAttribute('data-media-role') || '';
      const position = parseInt(slide.getAttribute('data-media-position') || '999', 10);
      if (role === 'swatch') {
        slide.hidden = true;
        slide.style.display = 'none';
        slide.setAttribute('aria-hidden', 'true');
        slide.removeAttribute('reveal');
        return;
      }

      const mediaColor = normalizeColorForMedia(slide.getAttribute('data-media-color') || '');
      const match = Boolean(mediaColor && mediaColor === normalized);
      if (match) {
        matchCount += 1;
        // Prefer lowest alt position so landing is truly first media
        if (position < firstPosition) {
          firstPosition = position;
          firstMatch = slide;
        }
      }
      slide.hidden = !match;
      slide.style.display = match ? '' : 'none';
      slide.style.opacity = '';
      slide.style.visibility = match ? '' : 'hidden';
      slide.setAttribute('aria-hidden', match ? 'false' : 'true');
      if (match) {
        slide.setAttribute('reveal', '');
      } else {
        slide.removeAttribute('reveal');
      }
    });

    if (matchCount === 0 || !firstMatch) return false;

    hydrateDeferredProductMedia(this);
    const zoomHost =
      this.closest('.shopify-section') ||
      this.closest('quick-add-dialog') ||
      this.closest('dialog');
    if (zoomHost) hydrateDeferredProductMedia(zoomHost);

    const slideshow = this.querySelector('slideshow-component');
    const scroller = slideshow?.querySelector('slideshow-slides');
    if (scroller instanceof HTMLElement) {
      const prevBehavior = scroller.style.scrollBehavior;
      const prevSnap = scroller.style.scrollSnapType;
      scroller.style.scrollBehavior = 'auto';
      scroller.style.scrollSnapType = 'none';
      // After hiding other colors, first match should sit at the start of the track
      scroller.scrollLeft = firstMatch.offsetLeft || 0;
      requestAnimationFrame(() => {
        scroller.scrollLeft = firstMatch.offsetLeft || 0;
        scroller.style.scrollBehavior = prevBehavior;
        scroller.style.scrollSnapType = prevSnap;
      });
    }

    // slideshow.select() indexes into *visible* slides only. Passing the raw DOM
    // index among all colors gets clamped to the last visible slide (end of gallery).
    if (slideshow && typeof /** @type {any} */ (slideshow).select === 'function') {
      try {
        /** @type {any} */ (slideshow).select(0, undefined, { animate: false });
      } catch {
        /* ignore */
      }
    }

    if (slideshow) {
      try {
        /** @type {any} */ (slideshow).current = 0;
      } catch {
        /* ignore */
      }
      const dots = /** @type {any} */ (slideshow).refs?.dots;
      if (Array.isArray(dots)) {
        dots.forEach((dot, index) => {
          if (dot instanceof HTMLElement) {
            dot.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
          }
        });
      }
    }

    return true;
  }

  /**
   * @param {string} colorValue
   * @returns {boolean}
   */
  #hasClientColorMatches(colorValue) {
    const normalized = normalizeColorForMedia(colorValue);
    if (!normalized) return false;

    const slides = this.querySelectorAll('slideshow-slide:not([data-gallery-clone])');
    for (const slide of slides) {
      if (!(slide instanceof HTMLElement)) continue;
      if ((slide.getAttribute('data-media-role') || '') === 'swatch') continue;
      const mediaColor = normalizeColorForMedia(slide.getAttribute('data-media-color') || '');
      if (mediaColor && mediaColor === normalized) return true;
    }
    return false;
  }

  /**
   * True when at least one non-swatch slide for this color is currently shown.
   * Used to skip full-width PDP gallery replace after the dedicated filter already ran.
   * @param {string} colorValue
   * @returns {boolean}
   */
  #hasVisibleClientColorMatches(colorValue) {
    const normalized = normalizeColorForMedia(colorValue);
    if (!normalized) return false;

    const slides = this.querySelectorAll('slideshow-slide:not([data-gallery-clone])');
    for (const slide of slides) {
      if (!(slide instanceof HTMLElement)) continue;
      if ((slide.getAttribute('data-media-role') || '') === 'swatch') continue;
      if (slide.hidden || slide.getAttribute('aria-hidden') === 'true') continue;
      if (slide.style.display === 'none') continue;
      const mediaColor = normalizeColorForMedia(slide.getAttribute('data-media-color') || '');
      if (mediaColor && mediaColor === normalized) return true;
    }
    return false;
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
